import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { useConnectivity } from '../contexts/ConnectivityContext';
import { db } from '../db';
import type { SyncPillTone } from '../components/patterns/SyncPill';

export interface SyncPillState {
  tone: SyncPillTone;
  label: string; // localised
}

/**
 * Unified app-wide sync indicator state.
 *
 * Returns a localised `{ tone, label }` derived from:
 *   - Connectivity (online vs offline/unreachable)
 *   - The total queued count across ALL local queue tables
 *     (operator combo starts/completes, spray session starts,
 *     spray sign-offs, scouting sessions).
 *
 * Tone precedence:
 *   - 'offline' if not online (regardless of queue size)
 *   - 'queued'  if online and queuedCount > 0
 *   - 'ok'      otherwise
 *
 * Consumers pass the returned values straight to `<SyncPill>`.
 *
 * Track: multi_operator_spray_sessions / Phase 6 / Task 6.11.
 */
export function useSyncPillState(): SyncPillState {
  const { isOnline } = useConnectivity();
  const { t } = useTranslation();

  const queuedCount =
    useLiveQuery(async () => {
      const [
        operatorStarts,
        operatorCompletes,
        sessionStarts,
        signOffs,
        scoutingSessions,
      ] = await Promise.all([
        db.operatorComboStarts
          .where('syncStatus')
          .anyOf(['pending', 'failed', 'syncing'])
          .count(),
        db.operatorComboCompletes
          .where('syncStatus')
          .anyOf(['pending', 'failed', 'syncing'])
          .count(),
        db.spraySessionStarts
          .where('syncStatus')
          .anyOf(['pending', 'failed', 'syncing'])
          .count(),
        db.spraySignOffs
          .where('syncStatus')
          .anyOf(['pending', 'failed', 'syncing'])
          .count(),
        db.scoutingSessions
          .where('syncStatus')
          .anyOf(['pending', 'failed', 'syncing'])
          .count(),
      ]);
      return (
        operatorStarts +
        operatorCompletes +
        sessionStarts +
        signOffs +
        scoutingSessions
      );
    }, [], 0) ?? 0;

  if (!isOnline) {
    return { tone: 'offline', label: t('shell.syncPill.offline') };
  }
  if (queuedCount > 0) {
    return {
      tone: 'queued',
      label: t('shell.syncPill.syncing', { count: queuedCount }),
    };
  }
  return { tone: 'ok', label: t('shell.syncPill.online') };
}
